import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { I18nService } from '../../core/i18n.service';
import { FunctionalityKey, User } from '../../core/models';
import { PaginationComponent } from '../../core/pagination.component';
import { TranslatePipe } from '../../core/translate.pipe';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, ReactiveFormsModule, TranslatePipe],
  template: `
    <section class="page-header">
      <div>
        <h1>{{ 'users.title' | t }}</h1>
        <p>{{ 'users.description' | t }}</p>
      </div>
    </section>

    <section class="panel">
      <form class="record-form" [formGroup]="form" (ngSubmit)="create()">
        <label>{{ 'login.username' | t }}<input formControlName="username"></label>
        <label>{{ 'users.fullName' | t }}<input formControlName="full_name"></label>
        <label>{{ 'users.email' | t }}<input formControlName="email"></label>
        <label>
          {{ 'users.role' | t }}
          <select formControlName="role">
            <option value="admin">{{ 'Admin' | t }}</option>
            <option value="procurement_officer">{{ 'Procurement Officer' | t }}</option>
            <option value="evaluator">{{ 'Evaluator' | t }}</option>
            <option value="finance">{{ 'Finance' | t }}</option>
            <option value="viewer">{{ 'Viewer' | t }}</option>
          </select>
        </label>
        <label>{{ 'login.password' | t }}<input type="password" formControlName="password"></label>
        <div class="form-actions">
          <button type="submit" [disabled]="form.invalid">{{ 'users.createUser' | t }}</button>
        </div>
      </form>
      <p class="error" *ngIf="error">{{ error }}</p>
    </section>

    <section class="panel" *ngIf="selectedUser">
      <div class="page-header compact-header">
        <div>
          <h2>{{ 'users.editAccess' | t }}</h2>
          <p>{{ 'users.editAccessFor' | t:{ name: selectedUser.full_name } }}</p>
        </div>
        <button type="button" class="secondary" (click)="cancelReset()">{{ 'common.cancel' | t }}</button>
      </div>
      <form class="record-form" [formGroup]="credentialForm" (ngSubmit)="resetCredentials()">
        <label>{{ 'login.username' | t }}<input formControlName="username"></label>
        <label>
          {{ 'users.role' | t }}
          <select formControlName="role">
            <option value="admin">{{ 'Admin' | t }}</option>
            <option value="procurement_officer">{{ 'Procurement Officer' | t }}</option>
            <option value="evaluator">{{ 'Evaluator' | t }}</option>
            <option value="finance">{{ 'Finance' | t }}</option>
            <option value="viewer">{{ 'Viewer' | t }}</option>
          </select>
        </label>
        <label class="checkbox-field">
          <input type="checkbox" formControlName="is_active">
          <span>{{ 'users.accountActive' | t }}</span>
        </label>
        <label>
          {{ 'users.newPassword' | t }}
          <input type="password" formControlName="password" autocomplete="new-password">
          <small>{{ 'users.passwordOptional' | t }}</small>
        </label>
        <label>
          {{ 'profile.confirmPassword' | t }}
          <input type="password" formControlName="confirm_password" autocomplete="new-password">
        </label>
        <fieldset class="access-fieldset">
          <legend>{{ 'users.functionalityAccess' | t }}</legend>
          <p class="muted">{{ 'users.adminFullAccess' | t }}</p>
          <label class="checkbox-field" *ngFor="let access of functionalityOptions">
            <input
              type="checkbox"
              [checked]="hasSelectedAccess(access.key)"
              [disabled]="credentialForm.controls.role.value === 'admin'"
              (change)="toggleAccess(access.key, $any($event.target).checked)">
            <span>{{ access.label | t }}</span>
          </label>
        </fieldset>
        <div class="form-actions">
          <button type="submit" [disabled]="credentialForm.invalid || passwordMismatch || resetting">{{ 'users.saveAccess' | t }}</button>
        </div>
      </form>
      <p class="error" *ngIf="passwordMismatch">{{ 'profile.passwordMismatch' | t }}</p>
      <p class="error" *ngIf="resetError">{{ resetError }}</p>
    </section>

    <p class="success" *ngIf="success">{{ success }}</p>

    <section class="panel">
      <div class="table-scroll">
        <table>
          <thead><tr><th>{{ 'login.username' | t }}</th><th>{{ 'users.name' | t }}</th><th>{{ 'users.email' | t }}</th><th>{{ 'users.role' | t }}</th><th>{{ 'common.active' | t }}</th><th>{{ 'common.actions' | t }}</th></tr></thead>
          <tbody>
            <tr *ngFor="let user of pagedUsers">
              <td>{{ user.username }}</td>
              <td>{{ user.full_name }}</td>
              <td>{{ user.email }}</td>
              <td><span class="status">{{ user.role | t }}</span></td>
              <td>{{ user.is_active ? ('common.yes' | t) : ('common.no' | t) }}</td>
              <td><button type="button" class="secondary" (click)="startReset(user)">{{ 'users.manageAccess' | t }}</button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <app-pagination
        [total]="users.length"
        [page]="page"
        [pageSize]="pageSize"
        [pageSizes]="pageSizes"
        (pageChange)="goToPage($event)"
        (pageSizeChange)="changePageSize($event)">
      </app-pagination>
    </section>
  `
})
export class UsersComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(I18nService);
  users: User[] = [];
  readonly pageSizes = [10, 25, 50, 100];
  readonly functionalityOptions: { key: FunctionalityKey; label: string }[] = [
    { key: 'dashboard', label: 'nav.dashboard' },
    { key: 'requisitions', label: 'nav.requisitionRegister' },
    { key: 'financial_management', label: 'nav.financialManagement' },
    { key: 'procurement', label: 'nav.procurementRegister' },
    { key: 'tendering', label: 'nav.tenderingProcessTracker' },
    { key: 'reports', label: 'nav.reports' }
  ];
  page = 1;
  pageSize = 10;
  error = '';
  success = '';
  resetError = '';
  resetting = false;
  selectedUser: User | null = null;
  form = this.fb.nonNullable.group({
    username: ['', Validators.required],
    full_name: ['', Validators.required],
    email: [''],
    role: ['viewer', Validators.required],
    password: ['', Validators.required]
  });
  credentialForm = this.fb.nonNullable.group({
    username: ['', Validators.required],
    role: ['viewer', Validators.required],
    is_active: [true],
    functionality_access: this.fb.nonNullable.control<FunctionalityKey[]>([]),
    password: ['', Validators.minLength(8)],
    confirm_password: ['']
  });

  ngOnInit(): void {
    this.load();
  }

  create(): void {
    if (this.form.invalid) return;
    this.api.createUser(this.form.getRawValue()).subscribe({
      next: () => {
        this.form.reset({ username: '', full_name: '', email: '', role: 'viewer', password: '' });
        this.page = 1;
        this.load();
      },
      error: (err) => this.error = err?.error?.detail ? this.i18n.t(err.error.detail) : this.i18n.t('users.unableCreate')
    });
  }

  get passwordMismatch(): boolean {
    const value = this.credentialForm.getRawValue();
    return !!value.password && value.password !== value.confirm_password;
  }

  startReset(user: User): void {
    this.selectedUser = user;
    this.success = '';
    this.resetError = '';
    this.credentialForm.reset({
      username: user.username,
      role: user.role,
      is_active: user.is_active,
      functionality_access: user.functionality_access ?? this.functionalityOptions.map(item => item.key),
      password: '',
      confirm_password: ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancelReset(): void {
    this.selectedUser = null;
    this.resetError = '';
  }

  resetCredentials(): void {
    if (!this.selectedUser || this.credentialForm.invalid || this.passwordMismatch) return;
    const target = this.selectedUser;
    const value = this.credentialForm.getRawValue();
    const payload: Record<string, unknown> = {
      username: value.username.trim(),
      role: value.role,
      is_active: value.is_active,
      functionality_access: value.role === 'admin'
        ? this.functionalityOptions.map(item => item.key)
        : value.functionality_access
    };
    if (value.password) payload['password'] = value.password;
    this.resetting = true;
    this.resetError = '';
    this.api.updateUser(target.id, payload).subscribe({
      next: (updated) => {
        this.resetting = false;
        this.selectedUser = null;
        this.success = this.i18n.t('users.credentialsSaved');
        this.load();
        if (target.id === this.auth.currentUser?.id && updated.username !== target.username) {
          this.auth.logout();
        }
      },
      error: (err) => {
        this.resetting = false;
        this.resetError = err?.error?.detail
          ? this.i18n.t(err.error.detail)
          : this.i18n.t('users.credentialsFailed');
      }
    });
  }

  hasSelectedAccess(key: FunctionalityKey): boolean {
    return this.credentialForm.controls.functionality_access.value.includes(key);
  }

  toggleAccess(key: FunctionalityKey, enabled: boolean): void {
    const current = this.credentialForm.controls.functionality_access.value;
    const next = enabled
      ? Array.from(new Set([...current, key]))
      : current.filter(item => item !== key);
    this.credentialForm.controls.functionality_access.setValue(next);
    this.credentialForm.controls.functionality_access.markAsDirty();
  }

  private load(): void {
    this.api.users().subscribe({
      next: (users) => {
        this.users = users;
        if (this.page > this.totalPages) this.page = this.totalPages;
      },
      error: () => this.error = this.i18n.t('users.failedLoad'),
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.users.length / this.pageSize));
  }

  get pageStart(): number {
    return this.users.length ? ((this.page - 1) * this.pageSize) + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min(this.page * this.pageSize, this.users.length);
  }

  get pagedUsers(): User[] {
    const start = (this.page - 1) * this.pageSize;
    return this.users.slice(start, start + this.pageSize);
  }

  changePageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  goToPage(page: number): void {
    this.page = Math.min(Math.max(1, page), this.totalPages);
  }
}
